import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { type ITabItem, type IType } from 'shared/ui'
import { CAMPAIGN_NAME_LIMIT } from 'shared/constants/limits'
import type { IParamsTestTrigger } from 'entities/Trigger/api/type'
import { TriggerApi } from 'entities/Trigger/api/trigger'
import { type ICreateCampaignStep } from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IParamsSendFrom } from 'entities/SendFrom'
import { openRcsNotSupportedFilesModal } from 'entities/Rcs'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { EnumTypeTrigger } from 'widgets/TriggerView/store/types'
import { CampaignScheduleStore } from 'pages/campaigns/ui/CampaignSchedule'
import { CampaignSMSStore } from 'pages/campaigns/ui/CampaignSMS'
import { CampaignRinglessStore } from 'pages/campaigns/ui/CampaignRingless'
import { CampaignSendFromStore } from 'pages/campaigns/ui/CampaignSendFrom'

export class CampaignMessageStore {
  nameInputStore
  campaignScheduleStore
  campaignSMSStore
  campaignRinglessStore
  campaignSendFromStore
  private _disposeTypeReaction: IReactionDisposer | null = null
  constructor(
    private _onDuplicate: (message: CampaignMessageStore) => void,
    private _onDelete: (uid: string) => void,
    private _entities: IEntitiesStore
  ) {
    this.nameInputStore = new NameInputStore({
      limit: CAMPAIGN_NAME_LIMIT,
    })
    this.campaignScheduleStore = new CampaignScheduleStore()
    this.campaignSMSStore = new CampaignSMSStore(this._entities)
    this.campaignRinglessStore = new CampaignRinglessStore(this._entities)
    this.campaignSendFromStore = new CampaignSendFromStore(this._entities)

    makeAutoObservable(this)
    this.reactionType()
  }

  dispose = () => {
    this._disposeTypeReaction?.()
  }

  get rcsInbox() {
    return this._entities.inboxesStore.rcsInbox
  }

  get tabs() {
    const tabs: ITabItem<IType>[] = [
      {
        key: 'sms',
        name: 'SMS',
      },
      {
        key: 'ringless',
        name: 'Ringless',
      },
    ]
    if (this._entities.inboxesStore.hasRcsInbox) {
      tabs.push({
        key: 'rcs',
        name: 'RCS',
        statusProps: {
          intent: 'purple',
          emphasis: 'high',
          title: 'New',
        },
      })
    }
    return tabs
  }

  get name() {
    return this.nameInputStore.name
  }
  get scheduleText() {
    return this.campaignScheduleStore.scheduleText
  }

  get params(): ICreateCampaignStep {
    return {
      id: this.id,
      name: this.name,
      send_from: this.sendFromParams,
      type: this.type,
      schedule: this.campaignScheduleStore.params,
      payload: this.typeParams,
      order: this.step,
    }
  }
  get typeParams() {
    if (this.type === 'sms' || this.type === 'rcs') {
      return this.campaignSMSStore.messageDataParams
    }
    return {
      message: '',
      media_url: this.campaignRinglessStore.ringlessMessageDataParams.media_url,
    }
  }

  get sendFromParams() {
    if (this.type === 'rcs') {
      const sendFromRCSParams: IParamsSendFrom = {
        type: 'from_rcs_sender',
        options: {
          team_id: this.rcsInbox?.id,
          number_id: null,
        },
      }

      return sendFromRCSParams
    }

    return this.campaignSendFromStore.params
  }

  init = async (input: ICreateCampaignStep, default_timezone?: string) => {
    this.id = input.id
    this.nameInputStore.init(input.name)
    this.campaignScheduleStore.init(input.schedule, default_timezone)
    this.type = input.type
    if (input.type === 'ringless') {
      if (input.payload.media_url?.[0]) {
        this.campaignRinglessStore.init(input.payload.media_url[0])
      }
    }
    if ((input.type === 'sms' || input.type === 'rcs') && 'message' in input.payload) {
      this.campaignSMSStore.init(input.payload.message, input.payload.media_url)
    }
    if (input.send_from) {
      this.campaignSendFromStore.init(input.send_from)
    }

    if (input.type === 'rcs') {
      this.campaignSMSStore.messageFieldStore.setIsRCS(true)
    }
  }

  id: number | null = null
  uid: string = nanoid()
  step = 0
  setStep = (step: number) => {
    this.step = step
    if (!this.nameInputStore.name) {
      this.nameInputStore.setName(`Step ${this.step}`)
    }
  }

  type: IType = 'sms'
  setType = (value: IType) => {
    const hasInvalidRcsAttachments =
      this.campaignSMSStore.messageFieldStore.hasInvalidRcsAttachments

    if (value === 'rcs' && hasInvalidRcsAttachments) {
      openRcsNotSupportedFilesModal({
        onContinue: () => {
          this.type = value
        },
      })

      return
    }

    this.type = value
  }

  onDuplicate = () => {
    this._onDuplicate(this)
  }
  onDelete = () => {
    this._onDelete(this.uid)
  }

  get testSMSParams() {
    const params: IParamsTestTrigger = {
      media_url: this.campaignSMSStore.messageDataParams.media_url,
      message: this.campaignSMSStore.messageDataParams.message,
    }

    if (this.campaignSendFromStore.number?.id)
      params.number_id = this.campaignSendFromStore.number?.id
    if (this.type === 'rcs' && this.rcsInbox?.id) params.team_id = this.rcsInbox?.id

    return params
  }

  onTestSMS = (params: IParamsTestTrigger) => TriggerApi.createTest(params)

  isCollapse = false
  setCollapse = (value: boolean) => {
    this.isCollapse = value
  }

  private get isError() {
    if (this.nameInputStore.error) {
      return true
    }
    if (this.type === 'sms' || this.type === 'rcs') {
      if (this.campaignSMSStore.error) {
        return true
      }
    }
    if (this.type === 'ringless') {
      if (this.campaignRinglessStore.error) {
        return true
      }
    }
    if (this.type === 'rcs') {
      if (!this.rcsInbox) {
        return true
      }
    } else if (this.campaignSendFromStore.error) {
      return true
    }
  }
  get error() {
    if (this.isError) {
      return 'Please complete all the fields'
    }
  }
  checkValidate = () => {
    let validate = true
    if (!this.nameInputStore.name.trim()) {
      validate = false
      this.nameInputStore.setError('Please enter message name')
    }
    if (this.type === 'sms' || this.type === 'rcs') {
      if (!this.campaignSMSStore.checkValidate()) {
        validate = false
      }
    }
    if (this.type === 'ringless') {
      if (!this.campaignRinglessStore.checkValidate()) {
        validate = false
      }
    }

    if (this.type === 'rcs') {
      if (!this.rcsInbox) {
        validate = false
      }
    } else if (!this.campaignSendFromStore.checkValidate()) {
      validate = false
    }

    return validate
  }

  setError = (path: string[], error: string) => {
    if (path[0] === 'name') {
      this.nameInputStore.setError(error)
    }
    if (path[0] === 'send_from') {
      this.campaignSendFromStore.setError(error)
    }
    if (path[0] === 'payload') {
      if (this.type === 'sms' || this.type === 'rcs') {
        this.campaignSMSStore.setError(error)
      }
      if (this.type === 'ringless') {
        this.campaignRinglessStore.setError(error)
      }
    }
  }

  reactionType = () => {
    this._disposeTypeReaction?.()
    this._disposeTypeReaction = reaction(
      () => this.type,
      (type) => {
        this.campaignSendFromStore.sendFromStore.setSkipUnverifiedNumbers(
          type !== EnumTypeTrigger.Ringless
        )

        this.campaignSMSStore.messageFieldStore.setIsRCS(type === 'rcs')

        if (type === 'rcs') {
          this.campaignSMSStore.messageFieldStore.validateRcsAttachments()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
